// "Cómo le fue": los números de un post que Atlas publicó, dentro del editor del calendario
// (el pedido de Nacho) — ADR-2026-10-29-dashboard-de-metricas-de-instagram. Si el post se borró
// en Instagram lo dice, en vez de dejar un "Ver en Instagram" que lleva a un link muerto.

import { useEffect, useState } from 'react';
import { BarChart3, Loader2 } from 'lucide-react';
import { leerPostDeInstagram, type Lectura, type PostDeInstagram } from '../../../../lib/instagramMetricas';
import { esReciente, numerosDelPost } from './metricas';

export function ComoLeFue({
  brandId,
  mediaId,
  onBorrado,
  grande = false,
}: {
  brandId: string | null;
  /** El id del post en Instagram. */
  mediaId: string;
  /**
   * Si Instagram dice que ya no existe (true) o que sí (false): el editor esconde o muestra su link.
   * Se avisa en las dos direcciones para que una lectura vieja no lo deje escondido.
   */
  onBorrado: (borrado: boolean) => void;
  /** En la vista del post publicado: arriba y con las tarjetas del detalle de Métricas. */
  grande?: boolean;
}) {
  const [lectura, setLectura] = useState<Lectura<PostDeInstagram | null> | null>(null);
  const [falla, setFalla] = useState(false);
  /** Sube con "Reintentar": vuelve a correr la lectura sin cerrar el post. */
  const [intento, setIntento] = useState(0);

  useEffect(() => {
    let vigente = true;
    setLectura(null);
    setFalla(false);
    leerPostDeInstagram(brandId, mediaId, { forzar: intento > 0 })
      .then((r) => {
        if (!vigente) return;
        setLectura(r.lectura);
        if (r.lectura.tipo === 'borrado') onBorrado(true);
        else if (r.lectura.tipo === 'ok') onBorrado(false);
      })
      .catch(() => vigente && setFalla(true));
    return () => {
      vigente = false;
    };
    // `onBorrado` es un callback del editor: cambiarlo no es motivo para volver a leer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId, mediaId, intento]);

  if (falla) {
    return (
      <p
        className={`flex flex-wrap items-center gap-2 text-neutral-500 animate-ficha-entra ${
          grande ? 'mt-5 rounded-xl bg-neutral-100 py-3 px-4 text-sm' : 'mt-3 text-xs'
        }`}
      >
        No pudimos leer cómo le fue a este post.
        <button type="button" onClick={() => setIntento((n) => n + 1)} className="font-medium text-brand-black underline">
          Reintentar
        </button>
      </p>
    );
  }
  if (!lectura) {
    return (
      <p className="mt-3 flex items-center gap-1.5 text-xs text-neutral-500">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Leyendo cómo le fue…
      </p>
    );
  }
  if (lectura.tipo === 'borrado') {
    return (
      <p
        className={`rounded-xl bg-neutral-100 text-neutral-600 animate-ficha-entra ${
          grande ? 'mt-5 py-3 px-4 text-sm' : 'mt-3 py-2.5 px-3 text-xs'
        }`}
      >
        <span className="font-semibold">Este post ya no está en Instagram.</span> Lo borraron desde Instagram o la cuenta
        conectada ahora es otra.
      </p>
    );
  }
  if (lectura.tipo !== 'ok' || !lectura.datos) {
    const mensaje =
      lectura.tipo === 'sin_conexion'
        ? 'Conectá tu Instagram en Mi marca para ver cómo le fue.'
        : lectura.tipo === 'ok'
          ? null
          : lectura.mensaje;
    return mensaje ? <p className="mt-3 text-xs text-neutral-500">{mensaje}</p> : null;
  }

  const numeros = numerosDelPost(lectura.datos);
  if (grande) {
    return (
      <section className="mt-5 rounded-2xl border border-neutral-200 bg-white p-5 shadow-sm animate-ficha-entra">
        <h2 className="flex items-center gap-2 text-base font-semibold text-brand-black">
          <BarChart3 className="w-4 h-4 text-neutral-500" /> Cómo le fue
        </h2>
        {numeros.length > 0 ? (
          <dl className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
            {numeros.map((n) => (
              <div key={n.etiqueta} className="rounded-xl bg-neutral-50 py-2.5 px-3">
                <dt className="text-[11px] font-medium uppercase leading-tight tracking-wide text-neutral-500">{n.etiqueta}</dt>
                <dd className="mt-0.5 text-lg font-semibold text-brand-black">{n.valor}</dd>
              </div>
            ))}
          </dl>
        ) : (
          <p className="mt-2 text-sm text-neutral-500">Instagram todavía no tiene números de este post.</p>
        )}
        {esReciente(lectura.datos.fecha) && (
          <p className="mt-3 text-xs text-neutral-500">Instagram sigue sumando números hasta 48 h después de publicar.</p>
        )}
      </section>
    );
  }
  return (
    <section className="mt-3 rounded-xl border border-neutral-200 bg-white py-3 px-4 animate-ficha-entra">
      <h3 className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-neutral-500">
        <BarChart3 className="w-3.5 h-3.5" /> Cómo le fue
      </h3>
      {numeros.length > 0 ? (
        <dl className="mt-2 grid grid-cols-3 gap-x-4 gap-y-2 sm:grid-cols-6">
          {numeros.map((n) => (
            <div key={n.etiqueta} className="min-w-0">
              <dt className="text-[11px] leading-tight text-neutral-500">{n.etiqueta}</dt>
              <dd className="text-sm font-semibold text-brand-black">{n.valor}</dd>
            </div>
          ))}
        </dl>
      ) : (
        <p className="mt-1 text-xs text-neutral-500">Instagram todavía no tiene números de este post.</p>
      )}
      {esReciente(lectura.datos.fecha) && (
        <p className="mt-2 text-xs text-neutral-500">Instagram sigue sumando números hasta 48 h después de publicar.</p>
      )}
    </section>
  );
}
